import { useEffect, useRef } from "react";
import { format } from "date-fns";
import {
  ISO_DATE,
  errorResult,
  getModelContext,
  registerMcpTool,
  type McpToolDescriptor,
} from "@/hooks/use-webmcp";

// WebMCP tool that lets an MCP-capable agent open the on-page BookingCheckout
// dialog ("Reserve your stay") pre-filled with the guest's dates, guest count,
// and any discount code. It only DRIVES the existing dialog state and opens it
// on the info step — it never advances the waiver / liability / payment steps
// and never charges. The human still fills in their contact details, signs the
// waivers, and pays through Stripe. No-op for browsers without WebMCP, so there
// is no behavior change for human visitors.

const MAX_GUESTS = 12;

const startCheckoutInputSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    checkIn: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description: "Check-in date in ISO format (YYYY-MM-DD).",
    },
    checkOut: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description: "Check-out date in ISO format (YYYY-MM-DD). Must be after checkIn.",
    },
    guests: {
      type: "integer",
      minimum: 1,
      maximum: MAX_GUESTS,
      description: "Number of guests staying (1-12).",
    },
    discountCode: {
      type: "string",
      description: "Optional promo / discount code to pre-fill in checkout.",
    },
  },
  required: ["checkIn", "checkOut", "guests"],
} as const;

export type StartCheckoutMcpPrefill = {
  checkIn: Date;
  checkOut: Date;
  guests: number;
  discountCode?: string;
};

export type StartCheckoutMcpHandlers = {
  /** Pre-fill the checkout dialog (dates, guests, discount code) and open it
   * on the info step. Does not advance steps or charge. */
  openWithPrefill: (prefill: StartCheckoutMcpPrefill) => void;
};

/**
 * Registers a single WebMCP tool ("start_cabin_booking_checkout") that opens the
 * on-page BookingCheckout ("Reserve your stay") dialog pre-filled with the
 * guest's dates, guest count, and any discount code. It stops on the info step
 * — it never advances the waiver / liability / payment steps and never charges.
 * No-ops entirely for browsers without WebMCP, so there is no behavior change
 * for human visitors.
 */
export function useStartCheckoutMcpTool(handlers: StartCheckoutMcpHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const mc = getModelContext();
    if (!mc) return;

    const tool: McpToolDescriptor = {
      name: "start_cabin_booking_checkout",
      description:
        "Open the Legendary Log Cabin \"Reserve your stay\" checkout dialog pre-filled with the guest's dates, guest count, and any discount code. Use this when a guest is ready to reserve a specific set of available dates. This does NOT complete the booking — it opens the on-page checkout on the first (contact info) step and never advances the waiver, liability, or payment steps and never charges. The guest still enters their details, signs the waivers, and pays through Stripe.",
      inputSchema: startCheckoutInputSchema as unknown as Record<string, unknown>,
      execute: async (args) => {
        const checkIn = typeof args.checkIn === "string" ? args.checkIn : "";
        const checkOut = typeof args.checkOut === "string" ? args.checkOut : "";
        const discountCode =
          typeof args.discountCode === "string" ? args.discountCode.trim() : "";

        if (!ISO_DATE.test(checkIn) || !ISO_DATE.test(checkOut)) {
          return errorResult("checkIn and checkOut must be ISO dates (YYYY-MM-DD).");
        }
        const from = new Date(checkIn + "T12:00:00");
        const to = new Date(checkOut + "T12:00:00");
        if (isNaN(from.getTime()) || isNaN(to.getTime())) {
          return errorResult("checkIn / checkOut are not valid dates.");
        }
        if (to.getTime() <= from.getTime()) {
          return errorResult("checkOut must be after checkIn.");
        }

        const guests = Math.floor(Number(args.guests));
        if (!Number.isFinite(guests) || guests < 1 || guests > MAX_GUESTS) {
          return errorResult(`guests must be a whole number between 1 and ${MAX_GUESTS}.`);
        }

        handlersRef.current.openWithPrefill({
          checkIn: from,
          checkOut: to,
          guests,
          discountCode: discountCode || undefined,
        });

        const structured = {
          checkIn,
          checkOut,
          guests,
          ...(discountCode ? { discountCode } : {}),
          step: "info",
          charged: false,
          note: "Checkout dialog opened on the contact-info step. Nothing was charged — the guest still enters their details, signs the waivers, and pays through Stripe.",
        };

        return {
          content: [
            {
              type: "text",
              text: `The "Reserve your stay" checkout is now open for ${guests} guest${guests !== 1 ? "s" : ""}, ${format(from, "MMM d")}–${format(to, "MMM d, yyyy")}${discountCode ? ` (code "${discountCode}" pre-filled)` : ""}. It's on the first step — nothing has been charged. The guest still needs to enter their details, sign the waivers, and pay through Stripe.`,
            },
          ],
          structuredContent: structured,
        };
      },
    };

    return registerMcpTool(tool);
  }, []);
}
